import { type ClientContext, ContextClient } from '~/services/contextClient';
import SegmentsClientFactory from './segmentsClient.factory';
import type { SegmentsClientInterface, Segment, GetSegmentsParams } from './segmentsClient.types';
import type { CursorPaginationResponse, QueryParams } from '~/services/api';
import type { AxiosPromise } from 'axios';
import type { ExtendedAxiosRequestConfig } from '~/services/BaseClient';

class SegmentsClient extends ContextClient<SegmentsClientInterface> {
    constructor() {
        super(SegmentsClientFactory);
    }

    getSegments(
        context: ClientContext,
        params?: GetSegmentsParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<CursorPaginationResponse<Segment>> {
        const client = this.getClient(context);
        return client.getSegments(params, config);
    }

    getSegmentsCount(context: ClientContext, params?: QueryParams): AxiosPromise<{ count: number }> {
        const client = this.getClient(context);
        return client.getSegmentsCount(params);
    }

    createSegment(context: ClientContext, segment: Segment): AxiosPromise<Segment> {
        const client = this.getClient(context);
        return client.createSegment(segment);
    }

    updateSegment(context: ClientContext, segmentId: number, segment: Segment): AxiosPromise<Segment> {
        const client = this.getClient(context);
        return client.updateSegment(segmentId, segment);
    }

    deleteSegment(context: ClientContext, segmentId: number): AxiosPromise<void> {
        const client = this.getClient(context);
        return client.deleteSegment(segmentId);
    }

    getSegment(context: ClientContext, segmentId: number): AxiosPromise<Segment> {
        const client = this.getClient(context);
        return client.getSegment(segmentId);
    }

    getSegmentContacts(
        context: ClientContext,
        segmentId: number,
        params?: QueryParams
    ): AxiosPromise<CursorPaginationResponse<Array<object>>> {
        const client = this.getClient(context);
        if (client.getSegmentContacts) {
            return client.getSegmentContacts(segmentId, params);
        }
        throw new Error(`Get segment contacts is not supported by ${client.constructor.name}`);
    }

    refreshSegment(context: ClientContext, segmentId: number): AxiosPromise<void> {
        const client = this.getClient(context);
        if (client.refreshSegment) {
            return client.refreshSegment(segmentId);
        }
        throw new Error(`Refresh segment is not supported by ${client.constructor.name}`);
    }

    getSegmentsContactsCount(
        context: ClientContext,
        params?: QueryParams
    ): AxiosPromise<{
        counts: Record<number, number>;
    }> {
        const client = this.getClient(context);
        return client.getSegmentsContactsCount(params);
    }

    getSegmentsGroupCounts(
        context: ClientContext,
        params?: QueryParams
    ): AxiosPromise<{
        counts: Record<number, number>;
    }> {
        const client = this.getClient(context);
        if (client.getSegmentsGroupCounts) {
            return client.getSegmentsGroupCounts(params);
        }
        throw new Error(`Get segments group counts is not supported by ${client.constructor.name}`);
    }

    dependencies(
        context: ClientContext,
        segmentId: number
    ): AxiosPromise<{
        data: object;
        truncated: boolean;
    }> {
        const client = this.getClient(context);
        if (client.dependencies) {
            return client.dependencies(segmentId);
        }
        throw new Error(`Dependencies is not supported by ${client.constructor.name}`);
    }

    getSegmentStatus(
        context: ClientContext,
        segmentId: number
    ): AxiosPromise<{
        status: string;
        last_refreshed_at: string | null;
    }> {
        const client = this.getClient(context);
        if (client.getSegmentStatus) {
            return client.getSegmentStatus(segmentId);
        }
        throw new Error(`Get segment status is not supported by ${client.constructor.name}`);
    }

    tagContacts(context: ClientContext, segmentId: number, data: { tags: string[] }): AxiosPromise<void> {
        const client = this.getClient(context);
        if (client.tagContacts) {
            return client.tagContacts(segmentId, data);
        }
        throw new Error(`Tag contacts is not supported by ${client.constructor.name}`);
    }

    countByAudienceType(context: ClientContext): AxiosPromise<
        Array<{
            count: number;
            audience_type: string;
        }>
    > {
        const client = this.getClient(context);
        if (client.countByAudienceType) {
            return client.countByAudienceType();
        }
        throw new Error(`Count by audience type is not supported by ${client.constructor.name}`);
    }

    countByRefreshType(context: ClientContext): AxiosPromise<Array<{ count: number; refresh_type: string }>> {
        const client = this.getClient(context);
        if (client.countByRefreshType) {
            return client.countByRefreshType();
        }
        throw new Error(`Count by refresh type is not supported by ${client.constructor.name}`);
    }
}

export default new SegmentsClient();
